import type { EcoleSelection, EcoleSharedProfile } from "@shared/ecole.types";
import { Users } from "lucide-react";
import { Badge } from "@/components/ds/Badge";
import { Card, CardHeader } from "@/components/ds/Card";
import { EmptyState } from "@/components/ds/EmptyState";
import { LinkButton } from "@/components/ds/LinkButton";
import { DECISION_LABEL, relativeDate } from "@/espace/labels";

/**
 * Les profils que l'école a envoyés, et ce que chaque entreprise en a dit.
 *
 * Une carte par envoi, un profil par ligne : la réponse de l'entreprise est ce que
 * le référent vient chercher, elle se lit sur la ligne du profil, sans ouvrir rien.
 */
export function SelectionsPage({ selections }: { selections: EcoleSelection[] }) {
	const envoyer = (
		<LinkButton to="/ecole/profils/nouvelle" tone="primary">
			Envoyer des profils
		</LinkButton>
	);

	if (selections.length === 0) {
		return (
			<div className="flex flex-col gap-5">
				<h1 className="t-h1 text-mg-heading">Profils envoyés</h1>
				<Card padded={false}>
					<EmptyState
						icon={<Users className="size-5" aria-hidden="true" />}
						title="Aucun profil envoyé"
						body="Choisissez des étudiants pour une offre reçue, ou pour une entreprise par son adresse."
						action={envoyer}
					/>
				</Card>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-5">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<h1 className="t-h1 text-mg-heading">Profils envoyés</h1>
				{envoyer}
			</div>
			<ul className="flex flex-col gap-4">
				{selections.map((selection) => (
					<li key={selection.id}>
						<SelectionCard selection={selection} />
					</li>
				))}
			</ul>
		</div>
	);
}

function SelectionCard({ selection }: { selection: EcoleSelection }) {
	const reponses = selection.profiles.filter((profile) => profile.decision).length;
	const destinataire =
		selection.contact.fullName ?? selection.contact.companyName ?? selection.contact.email;
	const pour = selection.offerJob ? `pour « ${selection.offerJob} »` : null;

	return (
		<Card padded={false}>
			<CardHeader
				title={selection.title}
				subtitle={[destinataire, pour, `envoyée ${relativeDate(selection.createdAt)}`]
					.filter(Boolean)
					.join(" · ")}
				action={
					<Badge tone={reponses === selection.profiles.length ? "success" : "neutral"}>
						{`${reponses} / ${selection.profiles.length} réponses`}
					</Badge>
				}
			/>
			<ul className="divide-y divide-mg-border-subtle">
				{selection.profiles.map((profile) => (
					<li
						key={profile.id}
						className="flex flex-col gap-2 px-6 py-4 md:flex-row md:items-start md:justify-between md:gap-3"
					>
						<div className="min-w-0 md:flex-1">
							<p className="t-h4 break-words text-mg-heading">{profile.fullName}</p>
							{profile.headline || profile.program ? (
								<p className="t-body text-mg-body">
									{[profile.headline, profile.program].filter(Boolean).join(" · ")}
								</p>
							) : null}
							{profile.comment ? (
								<p className="t-body mt-2 border-l-2 border-mg-border pl-3 text-mg-body">
									{profile.comment}
								</p>
							) : null}
						</div>
						<div className="md:shrink-0">
							<Reponse profile={profile} />
						</div>
					</li>
				))}
			</ul>
		</Card>
	);
}

/** La réponse de l'entreprise ; sans elle, si le profil a au moins été ouvert. */
function Reponse({ profile }: { profile: EcoleSharedProfile }) {
	if (profile.decision) {
		const decision = DECISION_LABEL[profile.decision];
		return <Badge tone={decision.tone}>{decision.short}</Badge>;
	}
	return <Badge tone="neutral">{profile.viewedAt ? "Vu, sans réponse" : "Pas encore vu"}</Badge>;
}
